// V3.1 — iteration on V3 focused on tightening the buy-box hierarchy:
//   • smaller product title with restored descenders
//   • no top routine-step chip (moved lower, near value props)
//   • no monthly subscription plan (only Membership + One-time)
//   • no "Use within 12 months of opening" fine-print in the hero
//   • Skin Analyzer moved below the CTA (not above it)
//   • value props centered, dermatology gets a medical icon
//   • Inter for prices/plan titles; IBM Plex Mono kept only for the
//     small eyebrows / counters / technical stamps
// Everything V3.1-only is gated by `P.v31` inside HeroSection.
import { EYE_REPAIR_V3 } from "@/data/eyeRepairV3";

const clone =
  typeof structuredClone === "function"
    ? structuredClone(EYE_REPAIR_V3)
    : JSON.parse(JSON.stringify(EYE_REPAIR_V3));

clone.v31 = true;

// V3.1 demo: bump the aggregate rating to 4.9 (from the site's 4.8) per
// client preference, and swap the review count in to match the live
// Judge.me widget so the replica renders authentic totals.
clone.rating = 4.9;
clone.reviewCount = 8761;

// V3.1: revert Complete-your-routine to the V2 horizontal-row layout
// (V3 introduced `routineWideBanner: true` which flips it into the
// wide 2-column grid of vertical product cards; V3.1 client explicitly
// wants the V2 treatment back).
clone.routineWideBanner = false;

// V3.1: repurpose the "Complete your routine" block as a Build-your-bundle
// experience. Product list is preserved but the title/eyebrow change so
// the section reads as a bundle builder with tiered incentives.
clone.routine = {
  ...clone.routine,
  eyebrow: "The more you add, the more you save",
  title: "Build your bundle",
};

// V3.1: Dr. Rivers story block gets a more editorial headline framed as
// an award callout. The two lines are longer than the V2/V3 default so
// `StoryDermatology` treats V3.1 titles with a slightly smaller serif
// italic (matching the Hinokitiol image caption).
clone.storyB = {
  ...clone.storyB,
  titleLines: [
    "Meet Dr. Rivers, the award-winning",
    "dermatologist behind Riversol",
  ],
};

// Drop the monthly subscription plan for this iteration.
if (Array.isArray(clone.plans)) {
  clone.plans = clone.plans.filter((p) => p.id !== "subscribe");
}

// Remove the "Use within 12 months of opening." line from the hero.
clone.perOrder = "";

// Middle-dot instead of slash so the label reads as one label, not two.
if (clone.routineStep?.label) {
  clone.routineStep.label = clone.routineStep.label.replace(/\s*\/\s*/g, " · ");
}

// V3.1: hoist Key ingredients above Targets in the Product Facts scroller.
if (Array.isArray(clone.facts?.rows)) {
  const rows = [...clone.facts.rows];
  const keyIdx = rows.findIndex(
    (r) => r?.label && /key\s*ingredient/i.test(r.label)
  );
  if (keyIdx > 0) {
    const [key] = rows.splice(keyIdx, 1);
    rows.unshift(key);
    clone.facts.rows = rows;
  }
}

// V3.1: hand-authored "what shoppers say" summary rendered above the two
// featured-review quote cards inside `ReviewsSection`. Editorial paragraph
// captures the aggregate sentiment, `themes` populate a row of proof pills,
// `distribution` drives a five-bar rating breakdown. All numbers add up to
// 100 so the bars render cleanly.
clone.reviews = {
  ...clone.reviews,
  summary: {
    eyebrow: "What shoppers say",
    paragraph:
      "Across 8,761 verified reviews, shoppers reach for Eye Repair Treatment because it visibly softens puffiness and brightens dark circles without irritating sensitive skin. Most notice smoother, firmer eye contours within two weeks and appreciate how quickly the lightweight formula sinks in under makeup.",
    themes: [
      { label: "Reduces puffiness", count: 2814 },
      { label: "Brightens dark circles", count: 2416 },
      { label: "Gentle on sensitive skin", count: 2020 },
      { label: "Fast, visible results", count: 1638 },
      { label: "Non-greasy finish", count: 1270 },
    ],
    distribution: [
      { stars: 5, percent: 88 },
      { stars: 4, percent: 8 },
      { stars: 3, percent: 2 },
      { stars: 2, percent: 1 },
      { stars: 1, percent: 1 },
    ],
  },
  // Real Judge.me widget replica — copy of the actual Riversol reviews
  // pulled from the anti-aging-trio product page for the demo. Kept as
  // static data so the preview never fails or racks up scraping cost.
  judgeme: {
    total: 8761,
    questionsTotal: 24,
    pages: 877,
    photos: [
      "https://review-images.judgeme.com/riversol/1784934493__5igjdg-then__original.jpeg?quality=80&width=160",
      "https://review-images.judgeme.com/riversol/1784934495__apxosa-now__original.jpeg?quality=80&width=160",
      "https://review-images.judgeme.com/riversol/1784166966__18aeqa-354071__original.jpg?quality=80&width=160",
      "https://review-images.judgeme.com/riversol/1783352360__img_3362__original.jpeg?quality=80&width=160",
      "https://review-images.judgeme.com/riversol/1782930473__img_7835__original.jpeg?quality=80&width=160",
      "https://review-images.judgeme.com/riversol/1778948101__1000154558__original.jpg?quality=80&width=160",
      "https://review-images.judgeme.com/riversol/1778821064__zjmn2q-_4440__original.jpeg?quality=80&width=160",
      "https://review-images.judgeme.com/riversol/1770237280__img_4142__original.jpeg?quality=80&width=160",
      "https://review-images.judgeme.com/riversol/1768037667__1000028697__original.jpg?quality=80&width=160",
      "https://review-images.judgeme.com/riversol/1766865757__1000062458__original.jpg?quality=80&width=160",
      "https://review-images.judgeme.com/riversol/1765633830__img_1575__original.jpeg?quality=80&width=160",
      "https://review-images.judgeme.com/riversol/1765633832__img_2181__original.jpeg?quality=80&width=160",
    ],
    items: [
      {
        rating: 5,
        author: "Linda",
        badge: "shop",
        date: "08/15/2026",
        title: null,
        body: "I just start to use it so far so good",
        source: "Review written in Shop App",
      },
      {
        rating: 5,
        author: "Anonymous",
        badge: "buyer",
        date: "08/14/2026",
        title: "Great product love it, face looks more brighter",
        body: "Great product love it, face looks more brighter and feels so soft.",
        reply:
          "Thank you so much for your wonderful review! We're thrilled to hear you love the product and see such great results. Enjoy your glowing skin!",
      },
      {
        rating: 5,
        author: "Janice C.",
        badge: "buyer",
        date: "08/12/2026",
        title: "Lovely to apply and noticeable difference",
        body: "Lovely to apply and noticeable difference.",
        reply:
          "Hi Janice, we're so glad to hear you love the Anti-Aging Trio! Thanks for sharing your experience.",
      },
      {
        rating: 5,
        author: "Camylle B.",
        badge: "buyer",
        date: "08/10/2026",
        title: "reparative spray",
        body:
          "I have used Riversol on my face for over a year and absolutely love it! Two days ago I used the reparative spray on my chest area. I have dry scaly skin on chest and arms. It is healing it already! My husband even noticed! Wow I love this stuff!",
        reply:
          "Hi Camylle, we're thrilled to hear how much you love our products! It's wonderful to know the reparative spray is helping your skin heal. Thank you for sharing your experience!",
      },
      {
        rating: 5,
        author: "Krista",
        badge: "shop",
        date: "08/07/2026",
        title: null,
        body: "Perfect for travel ans carry-on",
        source: "Review written in Shop App",
      },
      {
        rating: 5,
        author: "josephine a.",
        badge: "buyer",
        date: "08/05/2026",
        title: "GREAT PRODUCT",
        body:
          "I SEE GREAT IMPROVEMENT IN MY SKIN SINCE USING IT. IT IS ALMOST ONE YEAR AND I WILL KEEP BUYING IT. IT IS MY GO TO PRODUCT. PERFECT BY ITSELF OR UNDER MAKEUP.",
        reply:
          "Hi Josephine, we're thrilled to hear you're seeing such great results! Thank you for your continued trust in our Anti-Aging Trio with Peel. We're happy it's become your go-to. Looking forward to supporting your skincare journey!",
      },
      {
        rating: 5,
        author: "Nadine D.",
        badge: "buyer",
        date: "07/30/2026",
        title: "\"on it's way\"",
        body:
          "Just looking back at my purchases over the years and realize this one says \"on it's way\"",
        reply:
          "Hi Nadine, thanks for sharing! We're glad your order is on its way. We hope you enjoy the Anti-Aging To Go Set when it arrives!",
      },
    ],
  },
};

export const EYE_REPAIR_V31 = clone;
export default EYE_REPAIR_V31;
